import { useState, useEffect } from "react";
import type { ErrorLike } from "@apollo/client";
import { useAuth } from "../auth";
import { isUserSwitched } from "../utils";

export interface UseSetupGatingOptions {
  /** Whether setup gating applies at all, e.g. brand/mode gating. */
  enabled: boolean;
  organizations: unknown[];
  organizationsLoading: boolean;
  organizationsQueryData: unknown;
  organizationsQueryError: ErrorLike | undefined;
  tenantQueryError: ErrorLike | undefined;
  /** Clears any drafted setup data, e.g. localStorage entries. */
  resetSetupData: () => void;
}

export interface SetupGatingResult {
  needsSetup: boolean;
  checkingSetup: boolean;
  hasNetworkError: boolean;
  setNeedsSetup: (needs: boolean) => void;
  resetSetup: () => void;
}

/**
 * Decides whether the current tenant still needs to go through the setup
 * wizard, i.e. is enabled for setup gating and has no organizations yet.
 * Shared between backoffice and reseller.
 */
export function useSetupGating(
  options: UseSetupGatingOptions,
): SetupGatingResult {
  const {
    enabled,
    organizations,
    organizationsLoading,
    organizationsQueryData,
    organizationsQueryError,
    tenantQueryError,
    resetSetupData,
  } = options;

  const { isAuthenticated, loading: authLoading } = useAuth();
  const [needsSetup, setNeedsSetup] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [hasNetworkError, setHasNetworkError] = useState(false);

  useEffect(() => {
    if (authLoading || (isAuthenticated && organizationsLoading)) {
      setCheckingSetup(true);
      setHasNetworkError(false);
      return;
    }

    if (isAuthenticated && !organizationsLoading) {
      if (isUserSwitched()) {
        setNeedsSetup(false);
        setCheckingSetup(false);
        setHasNetworkError(false);
        return;
      }

      const error = organizationsQueryError ?? tenantQueryError;
      const hasError = error !== undefined;
      const hasData = organizationsQueryData !== undefined;

      const isNetworkError =
        hasError &&
        (!hasData ||
          (error &&
            "networkError" in error &&
            (error as { networkError?: unknown }).networkError !== undefined));

      if (isNetworkError) {
        setHasNetworkError(true);
        setNeedsSetup(false);
        setCheckingSetup(false);
        return;
      }

      setHasNetworkError(false);
      const hasOrganizations = organizations.length > 0;

      setNeedsSetup(enabled && !hasOrganizations);
      setCheckingSetup(false);
    } else if (!isAuthenticated) {
      setCheckingSetup(false);
      setNeedsSetup(false);
      setHasNetworkError(false);
    }
  }, [
    authLoading,
    isAuthenticated,
    organizationsLoading,
    organizations,
    organizationsQueryData,
    organizationsQueryError,
    tenantQueryError,
    enabled,
  ]);

  const resetSetup = () => {
    resetSetupData();
  };

  return {
    needsSetup,
    checkingSetup,
    hasNetworkError,
    setNeedsSetup,
    resetSetup,
  };
}
